import type { z } from 'zod';
import type {
  ScopeItemSchema,
  ScopeListEnvelope,
  ScopeCreatedEnvelope,
  ScopeItemEnvelope,
} from '@contract/scope';
import { apiClient } from './api';

/**
 * REV-821: the response types come from the vendored contract, which
 * revenant-api checks against its real routes. The hand-typed versions had
 * drifted: the item is the model as serialised, so its own timestamps are
 * camelCase (`createdAt`, `updatedAt`, not `created_at`/`updated_at`), `inc`
 * and `deletedAt` come through, a manual discovery entry carries `by`, and a
 * freshly created item has no `archived_at`/`archived_by` keys at all.
 */
export type ScopeItem = z.infer<typeof ScopeItemSchema>;
export type ScopeType = ScopeItem['scope_type'];
export type ScopeStatus = ScopeItem['status'];
export type DiscoverySourceEntry = ScopeItem['discovery_sources'][number];
export type DiscoverySource = DiscoverySourceEntry['source'];
export type ScopeListResponse = z.infer<typeof ScopeListEnvelope>;
export type ScopeCreatedResponse = z.infer<typeof ScopeCreatedEnvelope>;
type ScopeItemResponse = z.infer<typeof ScopeItemEnvelope>;

export interface CreateScopeItemData {
  canonical_identifier: string;
  scope_type?: ScopeType;
  source?: DiscoverySource;
  title?: string;
  /**
   * REV-500: pasted markup so a component with no URL of its own is still
   * testable. Omitted entirely when blank — sending '' on a re-add would read
   * as "clear the markup" rather than "no opinion".
   */
  markup?: string;
  metadata?: Record<string, unknown>;
}

export interface ListScopeItemsParams {
  status?: ScopeStatus;
  scope_type?: ScopeType;
  is_critical_path?: boolean;
  limit?: number;
  offset?: number;
}

export const scopeService = {
  async list(orgId: string, contextId: string, params: ListScopeItemsParams = {}) {
    const query: Record<string, unknown> = {};
    if (params.status) query.status = params.status;
    if (params.scope_type) query.scope_type = params.scope_type;
    if (typeof params.is_critical_path === 'boolean')
      query.is_critical_path = params.is_critical_path ? 'true' : 'false';
    if (typeof params.limit === 'number') query.limit = params.limit;
    if (typeof params.offset === 'number') query.offset = params.offset;

    const res = await apiClient.get<ScopeListResponse>(
      `/v1/organizations/${orgId}/contexts/${contextId}/scope`,
      query
    );
    return res;
  },

  async create(orgId: string, contextId: string, data: CreateScopeItemData) {
    const res = await apiClient.post<ScopeCreatedResponse>(
      `/v1/organizations/${orgId}/contexts/${contextId}/scope`,
      data
    );
    return res;
  },

  async getById(orgId: string, id: string) {
    const res = await apiClient.get<ScopeItemResponse>(
      `/v1/organizations/${orgId}/scope-items/${id}`
    );
    return res.data;
  },

  async toggleCriticalPath(orgId: string, id: string, isCriticalPath: boolean) {
    const res = await apiClient.patch<ScopeItemResponse>(
      `/v1/organizations/${orgId}/scope-items/${id}/critical-path`,
      { is_critical_path: isCriticalPath }
    );
    return res.data;
  },

  async archive(orgId: string, id: string) {
    const res = await apiClient.post<ScopeItemResponse>(
      `/v1/organizations/${orgId}/scope-items/${id}/archive`
    );
    return res.data;
  },

  async restore(orgId: string, id: string) {
    const res = await apiClient.post<ScopeItemResponse>(
      `/v1/organizations/${orgId}/scope-items/${id}/restore`
    );
    return res.data;
  },
};
